/**
 * The Loading component, used while resources load
 * Keep this component as similar as possible to the resource page importing it
 */
import React from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  InputGroup,
  InputGroupAddon,
  Input,
  Label,
  Button,
} from "reactstrap";
import FormattedNumber from "../../FormattedNumber";

import { FILEEDITOR } from "../../FileUploadEditorForm/utils/fileEditor";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

const SizeTab = ({ editorTypeId, updateSizes }, context) => {
  const language = useSelector((state) => {
    return state.i18nState.lang;
  });

  const { control } = useFormContext();
  const fileWidth = useWatch({ control, name: "width" });
  const fileHeight = useWatch({ control, name: "height" });
  const showSmallSizeWarning =
    isNaN(fileHeight) ||
    isNaN(fileWidth) ||
    parseInt(fileHeight) < 10 ||
    parseInt(fileWidth) < 10;

  const showMightBeDevidedHint =
    FILEEDITOR[editorTypeId] &&
    FILEEDITOR[editorTypeId].boundaries &&
    FILEEDITOR[editorTypeId].boundaries.maxNoDevide &&
    parseInt(fileHeight) > FILEEDITOR[editorTypeId].boundaries.maxNoDevide &&
    parseInt(fileWidth) > FILEEDITOR[editorTypeId].boundaries.maxNoDevide;

  // const defaultWarningSize = 120;
  // Disable Hint because it looks too aggresive since default values already fall into this
  const showSmallHint = false;
  // (fileWidth <
  //   (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
  //     ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
  //     : defaultWarningSize) &&
  //   fileHeight <
  //     (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
  //       ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
  //       : defaultWarningSize)) ||
  // fileWidth <
  //   (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
  //     ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
  //     : defaultWarningSize) /
  //     2 ||
  // fileHeight <
  //   (FILEEDITOR && editorTypeId && FILEEDITOR[editorTypeId]
  //     ? FILEEDITOR[editorTypeId].warningSize || defaultWarningSize
  //     : defaultWarningSize) /
  //     2;

  const defaultSizes = [
    { width: 150, height: 75 },
    { width: 200, height: 100 },
    { width: 300, height: 150 },
    { width: 400, height: 200 },
    { width: 500, height: 250 },
    { width: 600, height: 300 },
    { width: 800, height: 400 },
    { width: 1000, height: 500 },
    { width: 1200, height: 600 },
  ];
  return (
    <Container>
      <div className="h4">{context.t("size")}</div>
      <Row>
        {defaultSizes.map((size) => {
          return (
            <Col xs={{ size: 12, order: 2 }} md={{ size: 4, order: 1 }}>
              <div
                style={{ background: "#efefef" }}
                className={`p-2 mb-1 mb-md-3 rounded-lg pointer-light border ${
                  fileWidth === size.width && fileHeight === size.height
                    ? "border-success"
                    : "border-light"
                }`}
                onClick={() => updateSizes(size.width, size.height)}
              >
                <FormattedNumber
                  value={Math.round(size.width * 100) / 100}
                  language={language}
                  unit=""
                  decimals={0}
                />
                &nbsp;x&nbsp;
                <FormattedNumber
                  value={Math.round(size.height * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={0}
                />
                <span className="small ml-1">
                  (
                  <FormattedNumber
                    value={Math.round(size.width * 10) / 100}
                    language={language}
                    unit=""
                    decimals={1}
                  />
                  &nbsp;x&nbsp;
                  <FormattedNumber
                    value={Math.round(size.height * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={1}
                  />
                  )
                </span>
              </div>
            </Col>
          );
        })}
        <Col md={{ size: 8, order: 2 }} xs={{ size: 12, order: 1 }}>
          <div
            style={{ background: "#efefef" }}
            className={`p-2 mb-1 mb-md-3 rounded-lg pointer-light border  ${
              defaultSizes.find(
                (size) => fileWidth === size.width && fileHeight === size.height
              )
                ? "border-light"
                : "border-success"
            }`}
          >
            <Label>{context.t("custom size")}</Label>
            <InputGroup>
              <Controller
                name="width"
                control={control}
                render={({ field }) => <Input type="number" {...field} />}
              />
              <InputGroupAddon addonType="append">x</InputGroupAddon>
              <Controller
                name="height"
                control={control}
                render={({ field }) => <Input type="number" {...field} />}
              />
              <InputGroupAddon addonType="append">mm</InputGroupAddon>
            </InputGroup>
            <div>
              <span className="text-secondary">
                <FormattedNumber
                  value={Math.round(fileWidth * 100) / 100}
                  language={language}
                  unit=""
                  decimals={0}
                />
                &nbsp;x&nbsp;
                <FormattedNumber
                  value={Math.round(fileHeight * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={0}
                />
                &nbsp;=&nbsp;
                <strong>
                  <FormattedNumber
                    value={Math.round(fileWidth * 10) / 100}
                    language={language}
                    unit=""
                    decimals={1}
                  />
                  &nbsp;x&nbsp;
                  <FormattedNumber
                    value={Math.round(fileHeight * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={1}
                  />
                </strong>
              </span>
            </div>
          </div>
        </Col>
      </Row>
      {(showSmallHint || showSmallSizeWarning || showMightBeDevidedHint) && (
        <div className="h5 text-danger pt-4">⚠️ {context.t("Warning")}</div>
      )}
      {showSmallHint && (
        <p className="text-danger">
          {context.t(
            "The size of your design looks very small. Please double check you did not confuse MM with CM. Your selected measurements are:"
          )}
        </p>
      )}
      {showSmallSizeWarning && (
        <p className="text-danger">
          {context.t("The minimum size for height and width is 10mm ")}
        </p>
      )}
      {showMightBeDevidedHint && (
        <p className="text-danger">
          {context.t(
            "For technical reasons, your design may need to be divided into several parts, as both dimensions exceed the maximum manufacturable size. It can be installed without any visible gap."
          )}
        </p>
      )}
    </Container>
  );
};

/**  define proptype for the 'translation' function  */
SizeTab.contextTypes = {
  t: PropTypes.func,
};
export default SizeTab;
